//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { QueryRenderer, ReadyState } from "react-relay";
// team health dashboard imports
import { IRendererProps, IRendererContainer } from "./TeamHealthDashboard.d";
import { TeamHealthDashboard } from "./TeamHealthDashboard";
import { query } from "./TeamHealthDashboard.gql";
import * as s from "./TeamHealthDashboard.styles";
// other internals
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
// state
import { IReduxState } from "@reducers/root.reducer";
// components
import { CalloutError } from "@components/callout-error";
// util
import { maybeGetPrimaryTeamFromOrg } from "@util/team.util";
import { handleReadyStateError } from "@util/relay.util";
import { getCurrentOrg } from "@util/org.util";
import { getFetchInfo } from "@util/team-health.util";

//////////////////
// COMPONENT
//////////////////
export class TeamHealthDashboardRendererCmpt extends React.Component<IRendererProps> {
  private retry?: () => void;
  private refetchInterval?: ReturnType<typeof setInterval>;
  private cachedViewer: ISchema.IViewer | undefined;

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  public componentDidMount() {
    // if chrome extension, check open state to conditionally set interval, otherwise set interval
    this.handleChromeExtensionOpenStateChange(
      this.props.isChromeExtension ? this.props.chromeExtensionOpen : true,
    );
  }

  public componentWillUnmount() {
    this.clearRefetchInterval();
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IRendererProps) {
    if (
      nextProps.isChromeExtension &&
      nextProps.chromeExtensionOpen !== this.props.chromeExtensionOpen
    ) {
      this.handleChromeExtensionOpenStateChange(nextProps.chromeExtensionOpen);
    }
  }

  public shouldComponentUpdate() {
    // only update with initial mount b/c we only want this to handle the initial fetch
    // all updates and refetching are handled inside the Dashboard component
    return false;
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private clearRefetchInterval = () => {
    if (this.refetchInterval) {
      clearInterval(this.refetchInterval);
      delete this.refetchInterval;
    }
  };

  private setRefetchInterval = () => {
    this.clearRefetchInterval();
    this.refetchInterval = setInterval(() => {
      this.refetch();
    }, 15 * 60 * 1000);
  };

  private handleChromeExtensionOpenStateChange = (chromeExtensionOpen: boolean) => {
    // if closed, clear interval otherwise refetch + set interval
    if (!chromeExtensionOpen) {
      this.clearRefetchInterval();
    } else {
      this.refetch();
      this.setRefetchInterval();
    }
  };

  private refetch = () => {
    if (this.retry) {
      // rerender every 15 minutes to update calendar statuses
      this.retry();
    }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  public render() {
    const { org, chromeCalendarView, environment } = this.props;
    const { fetchVariables } = getFetchInfo(org.id, chromeCalendarView);
    const maybeTeam = maybeGetPrimaryTeamFromOrg(org);
    const maybeTeamId = maybeTeam ? maybeTeam.teamId : "";

    return (
      <QueryRenderer
        environment={environment}
        query={query}
        variables={{
          timeZone: fetchVariables.timeZone,
          rangeMin: fetchVariables.rangeMin,
          rangeMax: fetchVariables.rangeMax,
          teamId: maybeTeamId,
          hasTeams: !!maybeTeamId,
        }}
        cacheConfig={{}}
        render={(readyState: ReadyState) => {
          const freshViewer = readyState.props && readyState.props.viewer;
          const loadingForTheFirstTime = !this.cachedViewer && !freshViewer;
          const freshOrg = getCurrentOrg(freshViewer);

          // check for error or show a loader for first load
          if (readyState.error) {
            handleReadyStateError(readyState.error, "TeamHealthDashboardRenderer");

            return <CalloutError style={{ marginTop: 8 }} readyState={readyState} />;
          }

          if (readyState.retry) {
            this.retry = readyState.retry;
          }

          // loading for the first time
          if (loadingForTheFirstTime) {
            return (
              <div style={s.content}>
                <div style={s.background}>
                  <CircularProgress />
                </div>
              </div>
            );
          }

          // cache viewer so it doesn't flash on new loads
          if (freshViewer) {
            this.cachedViewer = freshViewer;
          }

          const freshUser = freshViewer && freshViewer.user;
          if (freshOrg && this.cachedViewer && freshUser) {
            return (
              <TeamHealthDashboard
                initialFetchVars={{
                  ...fetchVariables,
                  teamId: maybeTeamId,
                  hasTeams: !!maybeTeamId,
                }}
                org={freshOrg}
                user={freshUser}
              />
            );
          }

          return <span />;
        }}
      />
    );
  }
}

//////////////////
// REDUX
//////////////////
export const TeamHealthDashboardRenderer = connect(
  (state: IReduxState, _ownProps: IRendererContainer) => ({
    isChromeExtension: state.webExtension.isWebExtension,
    chromeExtensionOpen: state.webExtension.webExtensionOpen,
    chromeCalendarView: state.webExtension.calendarView,
  }),
  {},
)(TeamHealthDashboardRendererCmpt);
